import { useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, MapPin, Film, Tv, ChevronDown, Sparkles } from 'lucide-react';
import { getProfileUrl, getPosterUrl, calculateOurRating } from '../utils/helpers';
import { CURRENT_YEAR, DEFAULT_FILTERS, SORT_OPTIONS, GENRES, TV_GENRES } from '../utils/constants';
import { FilterBar, type FilterTab } from './FilterBar';
import { queryKeys } from '../lib/queryKeys';
import { apiFetch } from '../lib/api';
import { PersonDetailSkeleton } from './ui/Skeleton';
import type { Filters, SortOption, MediaType } from '../types/movie';

// Person details type (from your worker API)
interface PersonDetailsType {
  id: number;
  name: string;
  biography?: string;
  birthday?: string;
  deathday?: string;
  place_of_birth?: string;
  profile_path?: string | null;
  known_for_department?: string;
  also_known_as?: string[];
  combined_credits?: {
    cast: MediaCredit[];
    crew: MediaCredit[];
  };
}

interface MediaCredit {
  id: number;
  title?: string;
  name?: string;
  media_type: 'movie' | 'tv';
  poster_path: string | null;
  release_date?: string;
  first_air_date?: string;
  vote_average: number;
  character?: string;
  job?: string;
  genre_ids?: number[];
  original_language?: string;
  runtime?: number;
  episode_run_time?: number[];
  imdb_rating?: number;
  rt_rating?: number;
}

// Fetch function for person details
// Uses ONLY /person/:id endpoint (KV-cached, includes combined_credits from Supabase person_credits with character names)
// NOTE: /person/:id/filmography is deprecated and should not be used
async function fetchPersonDetails(personId: number): Promise<PersonDetailsType> {
  const personData = await apiFetch<PersonDetailsType>(
    `/person/${personId}`
  );

  return {
    ...personData,
    combined_credits: personData.combined_credits || { cast: [], crew: [] },
  };
}

export function PersonDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const personId = parseInt(id || '0', 10);

  // React Query for person data - route-keyed to prevent stale renders
  const {
    data: person,
    isLoading: loading,
    error: queryError,
  } = useQuery({
    queryKey: queryKeys.person.detail(personId),
    queryFn: () => fetchPersonDetails(personId),
    enabled: personId > 0,
  });

  const error = queryError instanceof Error ? queryError.message : queryError ? 'Failed to load person details' : null;

  // Single media type toggle (like home page)
  const [mediaType, setMediaType] = useState<MediaType>('movie');

  // Single filter state (like home page)
  const [filters, setFilters] = useState<Filters>({
    ...DEFAULT_FILTERS as Filters,
    sortBy: 'vote_average.desc' as SortOption,
  });

  // Filter state for directed movies section (for directors)
  const [directedFilters, setDirectedFilters] = useState<Filters>({
    ...DEFAULT_FILTERS as Filters,
    sortBy: 'vote_average.desc' as SortOption,
  });

  // Sort dropdown state
  const [sortOpen, setSortOpen] = useState(false);

  // Biography expanded state
  const [bioExpanded, setBioExpanded] = useState(false);

  // Get all movies
  const allMovies = useMemo(() => {
    if (!person) return [];
    return (person.combined_credits?.cast
      .filter((credit) => credit.media_type === 'movie' && credit.title) || []) as MediaCredit[];
  }, [person]);

  // Get all TV shows
  const allTVShows = useMemo(() => {
    if (!person) return [];
    return (person.combined_credits?.cast
      .filter((credit) => credit.media_type === 'tv' && credit.name) || []) as MediaCredit[];
  }, [person]);

  // Get directed movies
  const directedMovies = useMemo(() => {
    if (!person) return [];
    return person.combined_credits?.crew
      .filter((credit) => credit.media_type === 'movie' && credit.job === 'Director' && credit.title)
      .sort((a, b) => (b.vote_average || 0) - (a.vote_average || 0))
      .slice(0, 12) || [];
  }, [person]);

  // Filter directed movies based on directedFilters
  const filteredDirectedMovies = useMemo(() => {
    if (!person) return [];

    let filtered = [...directedMovies];

    // Apply filters (same logic as filteredMedia but for directed movies)
    // Genre filter
    if (directedFilters.genres.length > 0) {
      filtered = filtered.filter(item => {
        const itemGenres = item.genre_ids || [];
        return directedFilters.genres.some(g => itemGenres.includes(g));
      });
    }

    // Year filter
    if (directedFilters.yearRange[0] > 1920 || directedFilters.yearRange[1] < CURRENT_YEAR) {
      filtered = filtered.filter(item => {
        const year = item.release_date ? parseInt(item.release_date.split('-')[0]) : null;
        if (!year) return false;
        return year >= directedFilters.yearRange[0] && year <= directedFilters.yearRange[1];
      });
    }

    // Filmpl Rating filter
    if (directedFilters.ourRatingRange[0] > 0 || directedFilters.ourRatingRange[1] < 10) {
      filtered = filtered.filter(item => {
        const ourRating = calculateOurRating(item.imdb_rating, item.rt_rating);
        if (!ourRating) return false;
        return ourRating >= directedFilters.ourRatingRange[0] && ourRating <= directedFilters.ourRatingRange[1];
      });
    }

    // IMDb filter
    if (directedFilters.ratingRange[0] > 0 || directedFilters.ratingRange[1] < 10) {
      filtered = filtered.filter(item => {
        if (!item.imdb_rating) return false;
        return item.imdb_rating >= directedFilters.ratingRange[0] && item.imdb_rating <= directedFilters.ratingRange[1];
      });
    }

    // RT filter
    if (directedFilters.rtRatingRange[0] > 0 || directedFilters.rtRatingRange[1] < 100) {
      filtered = filtered.filter(item => {
        if (!item.rt_rating) return false;
        return item.rt_rating >= directedFilters.rtRatingRange[0] && item.rt_rating <= directedFilters.rtRatingRange[1];
      });
    }

    return filtered;
  }, [directedMovies, directedFilters, person]);

  // Filter and sort media based on current selection
  const filteredMedia = useMemo(() => {
    const items = mediaType === 'movie' ? allMovies : allTVShows;
    const isTV = mediaType === 'tv';

    let filtered = items.filter(item => {
      // Year filter
      const dateStr = isTV ? item.first_air_date : item.release_date;
      const year = dateStr ? parseInt(dateStr.split('-')[0]) : null;
      if (filters.yearRange[0] > 1920 || filters.yearRange[1] < CURRENT_YEAR) {
        if (!year || year < filters.yearRange[0] || year > filters.yearRange[1]) return false;
      }

      // Runtime filter
      if (filters.runtimeRange[0] > 0 || filters.runtimeRange[1] < 300) {
        const runtime = isTV ? item.episode_run_time?.[0] : item.runtime;
        if (runtime && (runtime < filters.runtimeRange[0] || runtime > filters.runtimeRange[1])) return false;
      }

      // Language filter
      if (filters.languages.length > 0) {
        if (!item.original_language || !filters.languages.includes(item.original_language)) return false;
      }

      // Genre filter
      if (filters.genres.length > 0) {
        const itemGenres = item.genre_ids || [];
        if (!filters.genres.some(g => itemGenres.includes(g))) return false;
      }

      // Filmpl Rating filter
      if (filters.ourRatingRange[0] > 0 || filters.ourRatingRange[1] < 10) {
        const ourRating = calculateOurRating(item.imdb_rating, item.rt_rating);
        if (!ourRating || ourRating < filters.ourRatingRange[0] || ourRating > filters.ourRatingRange[1]) return false;
      }

      // IMDB Rating filter
      if (filters.ratingRange[0] > 0 || filters.ratingRange[1] < 10) {
        if (!item.imdb_rating || item.imdb_rating < filters.ratingRange[0] || item.imdb_rating > filters.ratingRange[1]) return false;
      }

      // RT Rating filter
      if (filters.rtRatingRange[0] > 0 || filters.rtRatingRange[1] < 100) {
        if (!item.rt_rating || item.rt_rating < filters.rtRatingRange[0] || item.rt_rating > filters.rtRatingRange[1]) return false;
      }

      return true;
    });

    // Sort
    filtered.sort((a, b) => {
      const aOurRating = calculateOurRating(a.imdb_rating, a.rt_rating) || 0;
      const bOurRating = calculateOurRating(b.imdb_rating, b.rt_rating) || 0;
      const aDate = (isTV ? a.first_air_date : a.release_date) || '';
      const bDate = (isTV ? b.first_air_date : b.release_date) || '';

      switch (filters.sortBy) {
        case 'vote_average.desc':
        case 'our_rating.desc':
          return bOurRating - aOurRating;
        case 'vote_average.asc':
        case 'our_rating.asc':
          return aOurRating - bOurRating;
        case 'release_date.desc':
          return bDate.localeCompare(aDate);
        case 'release_date.asc':
          return aDate.localeCompare(bDate);
        case 'popularity.desc':
          return (b.vote_average || 0) - (a.vote_average || 0);
        case 'popularity.asc':
          return (a.vote_average || 0) - (b.vote_average || 0);
        default:
          return bOurRating - aOurRating;
      }
    });

    return filtered.slice(0, 48);
  }, [allMovies, allTVShows, mediaType, filters]);

  // Calculate age
  const calculateAge = () => {
    if (!person || !person.birthday) return null;
    const birth = new Date(person.birthday);
    const end = person.deathday ? new Date(person.deathday) : new Date();
    let age = end.getFullYear() - birth.getFullYear();
    const monthDiff = end.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && end.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const age = calculateAge();
  const isTV = mediaType === 'tv';

  const handleSortChange = (sortBy: SortOption) => {
    setFilters({ ...filters, sortBy });
    setSortOpen(false);
  };

  const activeSortLabel = SORT_OPTIONS.find(o => o.value === filters.sortBy)?.label || 'Highest Rated';

  // Skeleton while loading (no full-page block if cached)
  if (loading && !person) {
    return <PersonDetailSkeleton />;
  }

  // Error state (only if no person and not loading)
  if (!person) {
    return (
      <div className="min-h-screen bg-[#111111] flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error || 'Person not found'}</p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const hasMovies = allMovies.length > 0;
  const hasTVShows = allTVShows.length > 0;

  return (
    <div className="min-h-screen bg-[#111111] -mt-16 pt-16">
      {/* Back Button */}
      <div className="px-6 py-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
        >
          <ArrowLeft size={20} />
          <span className="text-sm font-medium">Back</span>
        </button>
      </div>

      {/* Person info */}
      <div className="px-6 pb-12">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Profile image - rounded-xl, not circular */}
          <div className="flex-shrink-0">
            <div className="w-[200px] aspect-[2/3] rounded-xl overflow-hidden shadow-xl bg-[#1A1F26]">
              {person.profile_path ? (
                <img
                  src={getProfileUrl(person.profile_path, 'large')}
                  alt={person.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-700 to-slate-800">
                  <span className="text-6xl">👤</span>
                </div>
              )}
            </div>
          </div>

          {/* Details */}
          <div className="flex-1">
            {/* Name */}
            <h1 className="text-3xl font-bold text-white mb-2">{person.name}</h1>

            {/* Role + Age */}
            <p className="text-slate-400 mb-2">
              {person.known_for_department}
              {person.birthday && (
                <>
                  {' • '}Born {new Date(person.birthday).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                  {age && !person.deathday && ` (${age} years old)`}
                </>
              )}
            </p>

            {/* Deathday if applicable */}
            {person.deathday && (
              <p className="text-red-400 text-sm mb-2">
                Died: {new Date(person.deathday).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
                {age && ` (${age} years old)`}
              </p>
            )}

            {/* Birthplace */}
            {person.place_of_birth && (
              <p className="flex items-center gap-1.5 text-slate-400 text-sm mb-6">
                <MapPin size={14} className="text-slate-500" />
                {person.place_of_birth}
              </p>
            )}

            {/* Biography */}
            {person.biography && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-white mb-3">Biography</h2>
                <p className="text-slate-300 leading-relaxed">
                  {bioExpanded || person.biography.length <= 600
                    ? person.biography
                    : `${person.biography.slice(0, 600)}...`}
                </p>
                {person.biography.length > 600 && (
                  <button
                    onClick={() => setBioExpanded(!bioExpanded)}
                    className="text-teal-400 hover:text-teal-300 text-sm mt-2"
                  >
                    {bioExpanded ? 'Show less' : 'Read more'}
                  </button>
                )}
              </div>
            )}

            {/* Also known as */}
            {person.also_known_as && person.also_known_as.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-slate-500 uppercase mb-2">Also Known As</h3>
                <div className="flex flex-wrap gap-2">
                  {person.also_known_as.slice(0, 5).map((name, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-[#1A1F26] text-slate-300 rounded-full text-sm"
                    >
                      {name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sections ordered by person's primary role */}
        {person.known_for_department === 'Directing' ? (
          <>
            {/* Directed Movies FIRST for directors */}
            {directedMovies.length > 0 && (
              <div className="mt-12">
                <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                  <Film size={24} className="text-cyan-400" />
                  Directed Movies
                </h2>

                <div className="mb-4">
                  <FilterBar
                    filters={directedFilters}
                    onChange={setDirectedFilters}
                    mediaType="movie"
                    hideTabs={['stream'] as FilterTab[]}
                  />
                </div>

                <p className="text-slate-400 text-sm mb-4">
                  {filteredDirectedMovies.length} movies found
                </p>

                {filteredDirectedMovies.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {filteredDirectedMovies.map((movie) => {
                      const ourRating = calculateOurRating((movie as MediaCredit).imdb_rating, (movie as MediaCredit).rt_rating);
                      return (
                        <Link
                          key={`dir-${movie.id}`}
                          to={`/movie/${movie.id}`}
                          className="group"
                        >
                          <div className="aspect-[2/3] rounded-lg overflow-hidden bg-[#1A1F26] mb-2 relative">
                            {movie.poster_path ? (
                              <img
                                src={getPosterUrl(movie.poster_path, 'medium')}
                                alt={movie.title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                loading="lazy"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-slate-600">
                                <span className="text-4xl">🎬</span>
                              </div>
                            )}
                            {ourRating && (
                              <div className="absolute top-2 right-2 bg-gradient-to-r from-purple-600 to-violet-600 px-2 py-1 rounded flex items-center gap-1">
                                <Sparkles size={10} className="text-purple-200" />
                                <span className="text-white text-xs font-bold">{ourRating.toFixed(1)}</span>
                              </div>
                            )}
                          </div>
                          <h3 className="text-sm font-medium text-white group-hover:text-cyan-400 transition-colors line-clamp-2">
                            {movie.title}
                          </h3>
                          <div className="flex items-center gap-1.5 mt-0.5 text-xs text-slate-500">
                            {movie.release_date && <span>{movie.release_date.split('-')[0]}</span>}
                            {movie.release_date && movie.runtime && <span>·</span>}
                            {movie.runtime && (
                              <span>
                                {`${Math.floor(movie.runtime / 60)}h${movie.runtime % 60 > 0 ? ` ${movie.runtime % 60}m` : ''}`}
                              </span>
                            )}
                            {(movie.release_date || movie.runtime) && movie.genre_ids?.[0] && <span>·</span>}
                            {movie.genre_ids?.[0] && (
                              <span className="truncate">{GENRES[movie.genre_ids[0]] || ''}</span>
                            )}
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-400">
                    <p>No directed movies match your filters</p>
                    <button
                      onClick={() => setDirectedFilters({ ...DEFAULT_FILTERS as Filters, sortBy: 'vote_average.desc' as SortOption })}
                      className="mt-2 text-cyan-400 hover:text-cyan-300"
                    >
                      Clear filters
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Filmography SECOND for directors */}
            {(hasMovies || hasTVShows) && (
              <div className="mt-12">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold text-white">Filmography</h2>
                  <div className="flex items-center bg-slate-700/50 rounded-full p-1">
                    <button
                      onClick={() => setMediaType('movie')}
                      disabled={!hasMovies}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                        mediaType === 'movie'
                          ? 'bg-gradient-to-r from-cyan-500 to-teal-500 text-white shadow-lg'
                          : hasMovies
                            ? 'text-slate-400 hover:text-white'
                            : 'text-slate-600 cursor-not-allowed'
                      }`}
                    >
                      <Film size={16} />
                      <span>Movies</span>
                      {hasMovies && <span className="text-xs opacity-75">({allMovies.length})</span>}
                    </button>
                    <button
                      onClick={() => setMediaType('tv')}
                      disabled={!hasTVShows}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                        mediaType === 'tv'
                          ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg'
                          : hasTVShows
                            ? 'text-slate-400 hover:text-white'
                            : 'text-slate-600 cursor-not-allowed'
                      }`}
                    >
                      <Tv size={16} />
                      <span>TV Shows</span>
                      {hasTVShows && <span className="text-xs opacity-75">({allTVShows.length})</span>}
                    </button>
                  </div>
                </div>

                <FilterBar
                  filters={filters}
                  onChange={setFilters}
                  mediaType={mediaType}
                  hideTabs={['stream'] as FilterTab[]}
                />

                <div className="flex items-center justify-between py-4">
                  <p className="text-slate-400 text-sm">
                    {filteredMedia.length} {isTV ? 'TV shows' : 'movies'} found
                  </p>
                  <div className="relative">
                    <button
                      onClick={() => setSortOpen(!sortOpen)}
                      className="flex items-center gap-2 px-4 py-2 bg-[#1A1F26] ring-1 ring-white/[0.08] rounded-lg text-slate-300 hover:text-white hover:ring-white/[0.12] transition-all"
                    >
                      <span className="text-sm">Sort: <span className={`font-medium ${isTV ? 'text-purple-400' : 'text-cyan-400'}`}>{activeSortLabel}</span></span>
                      <ChevronDown
                        size={16}
                        className={`transition-transform ${sortOpen ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {sortOpen && (
                      <div className="absolute right-0 top-full mt-2 w-56 bg-[#1A1F26] ring-1 ring-white/[0.08] rounded-xl shadow-xl shadow-black/40 z-50 overflow-hidden">
                        <div className="max-h-80 overflow-y-auto">
                          {SORT_OPTIONS.map((option) => (
                            <button
                              key={option.value}
                              onClick={() => handleSortChange(option.value)}
                              className={`w-full px-4 py-3 text-sm text-left transition-colors ${
                                filters.sortBy === option.value
                                  ? isTV ? 'bg-purple-500/20 text-purple-300' : 'bg-cyan-500/20 text-cyan-300'
                                  : 'text-slate-300 hover:bg-slate-700'
                              }`}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {filteredMedia.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {filteredMedia.map((item) => {
                      const ourRating = calculateOurRating(item.imdb_rating, item.rt_rating);
                      const title = isTV ? item.name : item.title;
                      const date = isTV ? item.first_air_date : item.release_date;
                      const route = isTV ? `/tv/${item.id}` : `/movie/${item.id}`;

                      return (
                        <Link
                          key={`${item.media_type}-${item.id}`}
                          to={route}
                          className="group"
                        >
                          <div className="aspect-[2/3] rounded-lg overflow-hidden bg-[#1A1F26] mb-2 relative">
                            {item.poster_path ? (
                              <img
                                src={getPosterUrl(item.poster_path, 'medium')}
                                alt={title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                loading="lazy"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-slate-600">
                                <span className="text-4xl">{isTV ? '📺' : '🎬'}</span>
                              </div>
                            )}
                            {isTV && (
                              <div className="absolute top-2 right-2 bg-purple-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
                                TV
                              </div>
                            )}
                            {(ourRating || item.imdb_rating || item.rt_rating) && (
                              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/70 to-transparent pt-6 pb-1.5 px-1">
                                <div className="flex justify-center gap-1">
                                  {ourRating && (
                                    <div className="bg-purple-600/90 px-1.5 py-0.5 rounded flex items-center gap-0.5">
                                      <span className="text-[9px]">⭐</span>
                                      <span className="text-[9px] font-bold text-white">{ourRating.toFixed(1)}</span>
                                    </div>
                                  )}
                                  {item.imdb_rating && (
                                    <div className="bg-yellow-600/90 px-1.5 py-0.5 rounded flex items-center gap-0.5">
                                      <span className="text-[9px]">★</span>
                                      <span className="text-[9px] font-bold text-white">{item.imdb_rating.toFixed(1)}</span>
                                    </div>
                                  )}
                                  {item.rt_rating && (
                                    <div className="bg-red-600/90 px-1.5 py-0.5 rounded flex items-center gap-0.5">
                                      <span className="text-[9px]">🍅</span>
                                      <span className="text-[9px] font-bold text-white">{item.rt_rating}%</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                          <h3 className={`text-sm font-medium text-white transition-colors line-clamp-2 ${isTV ? 'group-hover:text-purple-400' : 'group-hover:text-cyan-400'}`}>
                            {title}
                          </h3>
                          {item.character && (
                            <p className="text-xs text-teal-400 truncate">as {item.character}</p>
                          )}
                          <div className="flex items-center gap-1.5 mt-0.5 text-xs text-slate-500">
                            {date && <span>{date.split('-')[0]}</span>}
                            {date && item.runtime && <span>·</span>}
                            {item.runtime && (
                              <span>
                                {isTV
                                  ? `${item.runtime}m/ep`
                                  : `${Math.floor(item.runtime / 60)}h${item.runtime % 60 > 0 ? ` ${item.runtime % 60}m` : ''}`}
                              </span>
                            )}
                            {(date || item.runtime) && item.genre_ids?.[0] && <span>·</span>}
                            {item.genre_ids?.[0] && (
                              <span className="truncate">
                                {isTV
                                  ? (TV_GENRES[item.genre_ids[0]] || GENRES[item.genre_ids[0]] || '')
                                  : (GENRES[item.genre_ids[0]] || '')}
                              </span>
                            )}
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-400">
                    <p>No {isTV ? 'TV shows' : 'movies'} match your filters</p>
                    <button
                      onClick={() => setFilters({ ...DEFAULT_FILTERS as Filters, sortBy: 'vote_average.desc' as SortOption })}
                      className={`mt-2 ${isTV ? 'text-purple-400 hover:text-purple-300' : 'text-cyan-400 hover:text-cyan-300'}`}
                    >
                      Clear filters
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <>
            {/* Filmography FIRST for actors */}
            {(hasMovies || hasTVShows) && (
              <div className="mt-12">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold text-white">Filmography</h2>
                  <div className="flex items-center bg-slate-700/50 rounded-full p-1">
                    <button
                      onClick={() => setMediaType('movie')}
                      disabled={!hasMovies}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                        mediaType === 'movie'
                          ? 'bg-gradient-to-r from-cyan-500 to-teal-500 text-white shadow-lg'
                          : hasMovies
                            ? 'text-slate-400 hover:text-white'
                            : 'text-slate-600 cursor-not-allowed'
                      }`}
                    >
                      <Film size={16} />
                      <span>Movies</span>
                      {hasMovies && <span className="text-xs opacity-75">({allMovies.length})</span>}
                    </button>
                    <button
                      onClick={() => setMediaType('tv')}
                      disabled={!hasTVShows}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                        mediaType === 'tv'
                          ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg'
                          : hasTVShows
                            ? 'text-slate-400 hover:text-white'
                            : 'text-slate-600 cursor-not-allowed'
                      }`}
                    >
                      <Tv size={16} />
                      <span>TV Shows</span>
                      {hasTVShows && <span className="text-xs opacity-75">({allTVShows.length})</span>}
                    </button>
                  </div>
                </div>

                <FilterBar
                  filters={filters}
                  onChange={setFilters}
                  mediaType={mediaType}
                  hideTabs={['stream'] as FilterTab[]}
                />

                <div className="flex items-center justify-between py-4">
                  <p className="text-slate-400 text-sm">
                    {filteredMedia.length} {isTV ? 'TV shows' : 'movies'} found
                  </p>
                  <div className="relative">
                    <button
                      onClick={() => setSortOpen(!sortOpen)}
                      className="flex items-center gap-2 px-4 py-2 bg-[#1A1F26] ring-1 ring-white/[0.08] rounded-lg text-slate-300 hover:text-white hover:ring-white/[0.12] transition-all"
                    >
                      <span className="text-sm">Sort: <span className={`font-medium ${isTV ? 'text-purple-400' : 'text-cyan-400'}`}>{activeSortLabel}</span></span>
                      <ChevronDown
                        size={16}
                        className={`transition-transform ${sortOpen ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {sortOpen && (
                      <div className="absolute right-0 top-full mt-2 w-56 bg-[#1A1F26] ring-1 ring-white/[0.08] rounded-xl shadow-xl shadow-black/40 z-50 overflow-hidden">
                        <div className="max-h-80 overflow-y-auto">
                          {SORT_OPTIONS.map((option) => (
                            <button
                              key={option.value}
                              onClick={() => handleSortChange(option.value)}
                              className={`w-full px-4 py-3 text-sm text-left transition-colors ${
                                filters.sortBy === option.value
                                  ? isTV ? 'bg-purple-500/20 text-purple-300' : 'bg-cyan-500/20 text-cyan-300'
                                  : 'text-slate-300 hover:bg-slate-700'
                              }`}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {filteredMedia.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {filteredMedia.map((item) => {
                      const ourRating = calculateOurRating(item.imdb_rating, item.rt_rating);
                      const title = isTV ? item.name : item.title;
                      const date = isTV ? item.first_air_date : item.release_date;
                      const route = isTV ? `/tv/${item.id}` : `/movie/${item.id}`;

                      return (
                        <Link
                          key={`${item.media_type}-${item.id}`}
                          to={route}
                          className="group"
                        >
                          <div className="aspect-[2/3] rounded-lg overflow-hidden bg-[#1A1F26] mb-2 relative">
                            {item.poster_path ? (
                              <img
                                src={getPosterUrl(item.poster_path, 'medium')}
                                alt={title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                loading="lazy"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-slate-600">
                                <span className="text-4xl">{isTV ? '📺' : '🎬'}</span>
                              </div>
                            )}
                            {isTV && (
                              <div className="absolute top-2 right-2 bg-purple-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
                                TV
                              </div>
                            )}
                            {(ourRating || item.imdb_rating || item.rt_rating) && (
                              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/70 to-transparent pt-6 pb-1.5 px-1">
                                <div className="flex justify-center gap-1">
                                  {ourRating && (
                                    <div className="bg-purple-600/90 px-1.5 py-0.5 rounded flex items-center gap-0.5">
                                      <span className="text-[9px]">⭐</span>
                                      <span className="text-[9px] font-bold text-white">{ourRating.toFixed(1)}</span>
                                    </div>
                                  )}
                                  {item.imdb_rating && (
                                    <div className="bg-yellow-600/90 px-1.5 py-0.5 rounded flex items-center gap-0.5">
                                      <span className="text-[9px]">★</span>
                                      <span className="text-[9px] font-bold text-white">{item.imdb_rating.toFixed(1)}</span>
                                    </div>
                                  )}
                                  {item.rt_rating && (
                                    <div className="bg-red-600/90 px-1.5 py-0.5 rounded flex items-center gap-0.5">
                                      <span className="text-[9px]">🍅</span>
                                      <span className="text-[9px] font-bold text-white">{item.rt_rating}%</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                          <h3 className={`text-sm font-medium text-white transition-colors line-clamp-2 ${isTV ? 'group-hover:text-purple-400' : 'group-hover:text-cyan-400'}`}>
                            {title}
                          </h3>
                          {item.character && (
                            <p className="text-xs text-teal-400 truncate">as {item.character}</p>
                          )}
                          <div className="flex items-center gap-1.5 mt-0.5 text-xs text-slate-500">
                            {date && <span>{date.split('-')[0]}</span>}
                            {date && item.runtime && <span>·</span>}
                            {item.runtime && (
                              <span>
                                {isTV
                                  ? `${item.runtime}m/ep`
                                  : `${Math.floor(item.runtime / 60)}h${item.runtime % 60 > 0 ? ` ${item.runtime % 60}m` : ''}`}
                              </span>
                            )}
                            {(date || item.runtime) && item.genre_ids?.[0] && <span>·</span>}
                            {item.genre_ids?.[0] && (
                              <span className="truncate">
                                {isTV
                                  ? (TV_GENRES[item.genre_ids[0]] || GENRES[item.genre_ids[0]] || '')
                                  : (GENRES[item.genre_ids[0]] || '')}
                              </span>
                            )}
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-400">
                    <p>No {isTV ? 'TV shows' : 'movies'} match your filters</p>
                    <button
                      onClick={() => setFilters({ ...DEFAULT_FILTERS as Filters, sortBy: 'vote_average.desc' as SortOption })}
                      className={`mt-2 ${isTV ? 'text-purple-400 hover:text-purple-300' : 'text-cyan-400 hover:text-cyan-300'}`}
                    >
                      Clear filters
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Directed Movies SECOND for actors */}
            {directedMovies.length > 0 && (
              <div className="mt-12">
                <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                  <Film size={24} className="text-cyan-400" />
                  Directed Movies
                </h2>

                <div className="mb-4">
                  <FilterBar
                    filters={directedFilters}
                    onChange={setDirectedFilters}
                    mediaType="movie"
                    hideTabs={['stream'] as FilterTab[]}
                  />
                </div>

                <p className="text-slate-400 text-sm mb-4">
                  {filteredDirectedMovies.length} movies found
                </p>

                {filteredDirectedMovies.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {filteredDirectedMovies.map((movie) => {
                      const ourRating = calculateOurRating((movie as MediaCredit).imdb_rating, (movie as MediaCredit).rt_rating);
                      return (
                        <Link
                          key={`dir-${movie.id}`}
                          to={`/movie/${movie.id}`}
                          className="group"
                        >
                          <div className="aspect-[2/3] rounded-lg overflow-hidden bg-[#1A1F26] mb-2 relative">
                            {movie.poster_path ? (
                              <img
                                src={getPosterUrl(movie.poster_path, 'medium')}
                                alt={movie.title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                loading="lazy"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-slate-600">
                                <span className="text-4xl">🎬</span>
                              </div>
                            )}
                            {ourRating && (
                              <div className="absolute top-2 right-2 bg-gradient-to-r from-purple-600 to-violet-600 px-2 py-1 rounded flex items-center gap-1">
                                <Sparkles size={10} className="text-purple-200" />
                                <span className="text-white text-xs font-bold">{ourRating.toFixed(1)}</span>
                              </div>
                            )}
                          </div>
                          <h3 className="text-sm font-medium text-white group-hover:text-cyan-400 transition-colors line-clamp-2">
                            {movie.title}
                          </h3>
                          <div className="flex items-center gap-1.5 mt-0.5 text-xs text-slate-500">
                            {movie.release_date && <span>{movie.release_date.split('-')[0]}</span>}
                            {movie.release_date && movie.runtime && <span>·</span>}
                            {movie.runtime && (
                              <span>
                                {`${Math.floor(movie.runtime / 60)}h${movie.runtime % 60 > 0 ? ` ${movie.runtime % 60}m` : ''}`}
                              </span>
                            )}
                            {(movie.release_date || movie.runtime) && movie.genre_ids?.[0] && <span>·</span>}
                            {movie.genre_ids?.[0] && (
                              <span className="truncate">{GENRES[movie.genre_ids[0]] || ''}</span>
                            )}
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-400">
                    <p>No directed movies match your filters</p>
                    <button
                      onClick={() => setDirectedFilters({ ...DEFAULT_FILTERS as Filters, sortBy: 'vote_average.desc' as SortOption })}
                      className="mt-2 text-cyan-400 hover:text-cyan-300"
                    >
                      Clear filters
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
